// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";
import { CONSTRAINT_TYPE_LABELS } from "@/lib/constraints/humanize";

interface Props {
  event: any;
  updateEvent?: (patch: any) => void;
}

type Tier = "standard" | "priority" | "club_priority" | "showcase";

const TIER_LABELS: Record<Tier, string> = {
  standard: "Standard",
  priority: "Priority",
  club_priority: "Club Priority",
  showcase: "Showcase",
};

const TIER_STYLES: Record<Tier, string> = {
  standard: "bg-muted text-foreground border-border",
  priority: "bg-cyan-100 text-cyan-900 border-cyan-200",
  club_priority: "bg-blue-900 text-white border-blue-900",
  showcase: "bg-amber-100 text-amber-900 border-amber-200",
};

function titleCase(s: string): string {
  return String(s || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatParamValue(v: any): string {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function renderParams(params: any) {
  if (!params || typeof params !== "object") return null;
  const entries = Object.entries(params).filter(
    ([k]) => !k.startsWith("__") && !k.endsWith("__display")
  );
  if (entries.length === 0) return <span className="text-muted-foreground">No details</span>;
  return (
    <div className="space-y-0.5">
      {entries.map(([k, v]) => (
        <div key={k} className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{titleCase(k)}:</span>{" "}
          {formatParamValue(v)}
        </div>
      ))}
    </div>
  );
}

export function SchedulingRequestsSection({ event }: Props) {
  const { user } = useAuth();
  const eventId = event?.id;

  const [rows, setRows] = useState<any[]>([]);
  const [teamMap, setTeamMap] = useState<Record<string, string>>({});
  const [tierMap, setTierMap] = useState<Record<string, Tier>>({});
  const [templateMap, setTemplateMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"pending_review" | "approved" | "denied">(
    "pending_review"
  );
  const [tierFilter, setTierFilter] = useState<"all" | Tier>("all");
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchAll = async () => {
    if (!eventId) return;
    setLoading(true);
    const { data: constraints, error } = await (supabase as any)
      .from("schedule_constraints")
      .select("*")
      .eq("event_id", eventId)
      .is("deleted_at", null)
      // Unpaid (pending_payment) and superseded requests are not actionable.
      .in("status", ["pending_review", "approved", "partial", "denied"])
      .order("created_at", { ascending: false });

    if (error) {
      console.error(error);
      toast.error("Failed to load scheduling requests");
      setLoading(false);
      return;
    }
    const items = constraints || [];
    setRows(items);

    const teamIds = Array.from(new Set(items.map((c: any) => c.team_id).filter(Boolean)));
    const templateIds = Array.from(
      new Set(items.map((c: any) => c.template_id).filter(Boolean))
    );

    const [teamsRes, regsRes, tmplRes] = await Promise.all([
      teamIds.length
        ? (supabase as any).from("teams").select("id, display_name").in("id", teamIds)
        : Promise.resolve({ data: [] }),
      teamIds.length
        ? (supabase as any)
            .from("event_registrations_v2")
            .select("team_id, scheduling_tier")
            .eq("event_id", eventId)
            .in("team_id", teamIds)
        : Promise.resolve({ data: [] }),
      templateIds.length
        ? (supabase as any)
            .from("constraint_templates")
            .select("id, label")
            .in("id", templateIds)
        : Promise.resolve({ data: [] }),
    ]);

    const tm: Record<string, string> = {};
    for (const t of teamsRes.data || []) tm[t.id] = t.display_name;
    setTeamMap(tm);

    const rm: Record<string, Tier> = {};
    for (const r of regsRes.data || []) {
      if (r.team_id) rm[r.team_id] = (r.scheduling_tier as Tier) || "standard";
    }
    setTierMap(rm);

    const tpm: Record<string, string> = {};
    for (const t of tmplRes.data || []) tpm[t.id] = t.label;
    setTemplateMap(tpm);

    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const enriched = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        _team_name: r.team_id ? teamMap[r.team_id] || "Unknown team" : "—",
        _tier: (r.team_id && tierMap[r.team_id]) || "standard",
        _label:
          (r.template_id && templateMap[r.template_id]) ||
          CONSTRAINT_TYPE_LABELS[r.constraint_type] ||
          titleCase(r.constraint_type),
      })),
    [rows, teamMap, tierMap, templateMap]
  );

  const counts = useMemo(
    () => ({
      pending_review: enriched.filter((r) => r.status === "pending_review").length,
      approved: enriched.filter((r) => r.status === "approved").length,
      denied: enriched.filter((r) => r.status === "denied").length,
    }),
    [enriched]
  );

  const totalRevenue = useMemo(
    () =>
      enriched
        .filter((r) => r.status !== "denied")
        .reduce((sum, r) => sum + Number(r.fee_charged || 0), 0),
    [enriched]
  );

  const tierBreakdown = useMemo(() => {
    const map: Record<Tier, number> = {
      standard: 0,
      priority: 0,
      club_priority: 0,
      showcase: 0,
    };
    for (const r of enriched) map[r._tier as Tier] = (map[r._tier as Tier] || 0) + 1;
    return map;
  }, [enriched]);

  const visible = useMemo(() => {
    return enriched.filter((r) => {
      if (r.status !== activeTab) return false;
      if (tierFilter !== "all" && r._tier !== tierFilter) return false;
      return true;
    });
  }, [enriched, activeTab, tierFilter]);

  const processRefund = async (row: any) => {
    setBusyId(row.id);
    const { data, error } = await (supabase as any).functions.invoke(
      "refund-constraint-fee",
      { body: { constraint_id: row.id } }
    );
    setBusyId(null);
    if (error || data?.error) {
      const msg = data?.error || error?.message || "Refund failed";
      toast.error(msg);
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, refund_status: "failed" } : r))
      );
      return;
    }
    toast.success("Refund issued");
    setRows((prev) =>
      prev.map((r) =>
        r.id === row.id
          ? { ...r, refund_status: "refunded", refunded_at: new Date().toISOString() }
          : r
      )
    );
  };

  const review = async (row: any, decision: "approved" | "denied") => {
    setBusyId(row.id);
    const patch: any = {
      status: decision,
      reviewed_by: user?.id ?? null,
      reviewed_at: new Date().toISOString(),
    };
    const fee = Number(row.fee_charged || 0);
    if (decision === "denied" && fee > 0) {
      patch.refund_status = "pending";
    }
    const { error } = await (supabase as any)
      .from("schedule_constraints")
      .update(patch)
      .eq("id", row.id);
    if (error) {
      setBusyId(null);
      console.error(error);
      toast.error(`Failed to ${decision === "approved" ? "approve" : "deny"} request`);
      return;
    }
    toast.success(decision === "approved" ? "Request approved" : "Request denied");
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...patch } : r)));
    setBusyId(null);

    if (decision === "denied" && fee > 0) {
      await processRefund({ ...row, ...patch });
    }

    // Tier-level refund check (no-op unless team is fully resolved w/ zero approved)
    try {
      const { data: tData, error: tErr } = await (supabase as any).functions.invoke(
        "refund-tier-fee",
        { body: { constraint_id: row.id } }
      );
      if (tErr) {
        toast.error(tErr.message || "Tier refund check failed");
      } else if (tData?.ok) {
        const amt = Number(tData.refunded_amount || 0).toFixed(2);
        const teamName = row._team_name || teamMap[row.team_id] || "team";
        toast.success(
          `Tier fee of $${amt} refunded to ${teamName} — all of their scheduling requests were denied.`
        );
      } else if (tData?.error) {
        toast.error(tData.error);
      }
    } catch (e: any) {
      console.error("refund-tier-fee invoke failed", e);
    }
  };

  if (!eventId) {
    return (
      <p className="text-sm text-muted-foreground">
        Save the event first to manage scheduling requests.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-muted-foreground">
          Review the scheduling constraints teams submitted at registration. Approve to
          honor them in the scheduler; deny if they can't be accommodated.
        </p>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="border border-border rounded-lg p-4 bg-card">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Total requests
          </div>
          <div className="text-2xl font-semibold mt-1">{enriched.length}</div>
        </div>
        <div className="border border-border rounded-lg p-4 bg-card">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Request-fee revenue
          </div>
          <div className="text-2xl font-semibold mt-1">
            ${totalRevenue.toFixed(2)}
          </div>
        </div>
        <div className="border border-border rounded-lg p-4 bg-card">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            By tier
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {(Object.keys(TIER_LABELS) as Tier[]).map((t) => (
              <Badge
                key={t}
                variant="outline"
                className={`${TIER_STYLES[t]} text-xs`}
              >
                {TIER_LABELS[t]}: {tierBreakdown[t] || 0}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList>
          <TabsTrigger value="pending_review">
            Pending Review ({counts.pending_review})
          </TabsTrigger>
          <TabsTrigger value="approved">Approved ({counts.approved})</TabsTrigger>
          <TabsTrigger value="denied">Denied ({counts.denied})</TabsTrigger>
        </TabsList>

        {/* Tier filter */}
        <div className="flex items-center gap-2 flex-wrap mt-4">
          <span className="text-xs text-muted-foreground mr-1">Tier:</span>
          {(["all", "standard", "priority", "club_priority", "showcase"] as const).map(
            (t) => (
              <button
                key={t}
                onClick={() => setTierFilter(t as any)}
                className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                  tierFilter === t
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border hover:bg-secondary"
                }`}
              >
                {t === "all" ? "All" : TIER_LABELS[t as Tier]}
              </button>
            )
          )}
        </div>

        {(["pending_review", "approved", "denied"] as const).map((tab) => (
          <TabsContent key={tab} value={tab} className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : visible.length === 0 ? (
              <div className="border border-dashed border-border rounded-lg py-12 text-center text-sm text-muted-foreground">
                No requests in this view.
              </div>
            ) : (
              <div className="space-y-2">
                {visible.map((r) => {
                  const fee = Number(r.fee_charged || 0);
                  return (
                    <div
                      key={r.id}
                      className="border border-border rounded-lg p-4 bg-card flex flex-col md:flex-row md:items-start md:justify-between gap-3"
                    >
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="flex items-center flex-wrap gap-2">
                          <span className="font-semibold text-foreground">
                            {r._team_name}
                          </span>
                          <Badge
                            variant="outline"
                            className={TIER_STYLES[r._tier as Tier]}
                          >
                            {TIER_LABELS[r._tier as Tier]}
                          </Badge>
                          {r.status === "denied" &&
                            r.refund_status === "pending" && (
                              <Badge
                                variant="outline"
                                className="bg-amber-100 text-amber-900 border-amber-200"
                              >
                                Refund pending
                              </Badge>
                            )}
                          {r.status === "denied" &&
                            r.refund_status === "refunded" && (
                              <Badge
                                variant="outline"
                                className="bg-green-100 text-green-900 border-green-200"
                              >
                                Refunded
                              </Badge>
                            )}
                          {r.status === "denied" &&
                            r.refund_status === "failed" && (
                              <Badge
                                variant="outline"
                                className="bg-red-100 text-red-900 border-red-200"
                              >
                                Refund failed
                              </Badge>
                            )}
                        </div>
                        <div className="text-sm text-foreground">{r._label}</div>
                        {renderParams(r.parameters)}
                        <div className="text-xs text-muted-foreground pt-1">
                          Submitted{" "}
                          {r.created_at
                            ? format(new Date(r.created_at), "MMM d, yyyy")
                            : "—"}{" "}
                          •{" "}
                          <span className="font-medium text-foreground">
                            {fee > 0 ? `$${fee.toFixed(2)}` : "Included"}
                          </span>
                        </div>
                      </div>

                      {tab === "pending_review" && (
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === r.id}
                            onClick={() => review(r, "denied")}
                          >
                            Deny
                          </Button>
                          <Button
                            size="sm"
                            disabled={busyId === r.id}
                            onClick={() => review(r, "approved")}
                          >
                            {busyId === r.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              "Approve"
                            )}
                          </Button>
                        </div>
                      )}

                      {tab === "denied" &&
                        fee > 0 &&
                        (r.refund_status === "pending" ||
                          r.refund_status === "failed") && (
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busyId === r.id}
                              onClick={() => processRefund(r)}
                            >
                              {busyId === r.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                "Retry refund"
                              )}
                            </Button>
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
